import React, { useState } from 'react'
import { _FORM_ITEM_LAYOUT, _TAIL_FORM_ITEM_LAYOUT } from '../../../../constants';
import { StyledForm, StyledFormUpload, StyledTabs } from './style';
import { App, Button, Col, DatePicker, Divider, Form, Input, Row, Upload, UploadProps } from 'antd';
import UserLookup from '../../../Common/Form/UserLookup';
import { InboxOutlined, UploadOutlined } from '@ant-design/icons';
import DayForm from './DayForm';
import { useCreateHRV } from '../../../../hooks/admin/useHrv';
import { useAdminSignInAsProfessional } from '../../../../hooks/admin/useAdminProfessional';
import { useToggleProfessionalViewable } from '../../../../hooks/admin/useListAnalysesView';
import { useToggleClientViewable } from '../../../../hooks/professionals/useAnalyses';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../redux/store';
import { getNumericId } from '../../../../helpers';

type TargetKey = React.MouseEvent | React.KeyboardEvent | string;

const initialItems = [
  { label: 'Day 1', children: <DayForm formNum={1} />, key: '1' },
  { label: 'Day 2', children: <DayForm formNum={2} />, key: '2' },
];

interface HRVFormProps {
  onSuccess?: () => void;
  initialProfessionalId?: string | number;
  initialClientId?: string;
}

const HRVForm: React.FC<HRVFormProps> = ({ onSuccess, initialProfessionalId, initialClientId }) => {
  const { create: createHRV } = useCreateHRV();
  const { toggle } = useToggleProfessionalViewable();
  const { toggleClientViewable } = useToggleClientViewable();
  const token = useSelector((state: any) => state.auth.token);
  const user = useSelector((state: RootState) => state.auth.user);
  const professionals = useSelector((state: any) => state.professionals.professionals || []);
  const { signInAsProfessional } = useAdminSignInAsProfessional();
  const [form] = StyledForm.useForm();
  const [activeKey, setActiveKey] = useState(initialItems[0].key);
  const [items, setItems] = useState(initialItems);
  const { message: antdMessage } = App.useApp();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fileObj, setFileObj] = useState<any>(null);
  const [specialistReportObj, setSpecialistReportObj] = useState<any>(null);
  const submitLock = React.useRef(false);

  const onChange = (newActiveKey: string) => {
    setActiveKey(newActiveKey);
  };

  const add = () => {
    const newPanes = [...items];
    const newActiveKey = items.length + 1;
    newPanes.push({ label: `Day ${newActiveKey}`, children: <DayForm formNum={newActiveKey} />, key: `newTab${newActiveKey.toString()}` });
    setItems(newPanes);
  };

  const remove = (targetKey: TargetKey) => {
    let newActiveKey = activeKey;
    let lastIndex = -1;
    items.forEach((item, i) => {
      if (item.key === targetKey) {
        lastIndex = i - 1;
      }
    });
    const newPanes = items.filter((item) => item.key !== targetKey);
    if (newPanes.length && newActiveKey === targetKey) {
      if (lastIndex >= 0) {
        newActiveKey = newPanes[lastIndex].key;
      } else {
        newActiveKey = newPanes[0].key;
      }
    }
    setItems(newPanes);
    setActiveKey(newActiveKey);
  };

  const onEdit = (
    targetKey: React.MouseEvent | React.KeyboardEvent | string,
    action: 'add' | 'remove',
  ) => {
    if (action === 'add') {
      add();
    } else {
      remove(targetKey);
    }
  };

  const onFinish = async (values: any) => {
    console.log('HRVForm - onFinish triggered');
    if (submitLock.current) {
      console.warn('HRVForm - Double submission blocked by lock');
      return;
    }
    submitLock.current = true;
    console.log('HRVForm - Submission lock acquired, starting upload...');
    setIsSubmitting(true);
    const { professional, client, attachmentFile, specialistReportFile, reportNumber, dateOfAnalysis, durationOfAnalysis } = values;

    const hrvDays: any[] = [];
    Object.keys(values).forEach(key => {
      if (key.startsWith('form')) {
        const dayNum = key.replace('form', '');
        hrvDays.push({
          dayNumber: parseInt(dayNum, 10),
          ...values[key]
        });
      }
    });

    const hrvData: { [key: string]: any } = {
      clientId: getNumericId(client),
      reportNumber,
      attachment: fileObj,
      specialistReport: specialistReportObj,
      dateOfAnalysis: dateOfAnalysis ? dateOfAnalysis.format('YYYY-MM-DD') : undefined,
      durationOfAnalysis: parseInt(durationOfAnalysis, 10),
      data: hrvDays
    }

    try {
      const result = await createHRV({
        professionalId: professional,
        hrvData: hrvData
      });

      if (result?.inputHrv?.id) {
        const analysesId = result.inputHrv.id; // Use Global ID (GID) for toggles as per professional dashboard fix
        const professionalId = getNumericId(professional);
        const clientId = getNumericId(client);

        // Toggle Professional Viewable (Admin action with Admin token)
        await toggle({
          analysesId,
          analysesType: 'hrv',
          professionalId
        });

        const isAdmin = user?.__typename === 'Admin' || !(user as any)?.code;

        if (isAdmin) {
          const selectedProf = professionals.find((p: any) => getNumericId(p.id) === professionalId);
          if (selectedProf?.email) {
            try {
              const proSignIn = await signInAsProfessional(selectedProf.email);
              if (proSignIn?.token) {
                // Toggle Client Viewable using the same hook/approach that worked elsewhere
                await toggleClientViewable(clientId.toString(), analysesId, 'hrv', proSignIn.token);
              }
            } catch (ghostErr) {
              console.warn('Ghost login failed for toggleClient:', ghostErr);
            }
          }
        }
      }

      antdMessage.success('HRV data saved successfully');
      console.log('HRVForm - Upload successful');
      form.resetFields();
      if (onSuccess) {
        onSuccess();
      }
    } catch (err: any) {
      console.error('onSubmitHRV Error', err);
      antdMessage.error(err.message || 'Failed to save HRV data');
    } finally {
      submitLock.current = false;
      setIsSubmitting(false);
    }
  };

  const props: UploadProps = {
    name: 'file',
    multiple: false,
    beforeUpload: (file) => {
      setFileObj(file);
      form.setFieldsValue({ attachmentFile: file });
      return false;
    },
    onRemove: () => {
      setFileObj(null);
      form.setFieldsValue({ attachmentFile: undefined });
    }
  };

  const specialistReportProps: UploadProps = {
    name: 'specialistReport',
    multiple: false,
    beforeUpload: (file) => {
      setSpecialistReportObj(file);
      form.setFieldsValue({ specialistReportFile: file });
      return false;
    },
    onRemove: () => {
      setSpecialistReportObj(null);
      form.setFieldsValue({ specialistReportFile: undefined });
    }
  };

  return (
    <StyledForm
      form={form}
      layout="vertical"
      onFinish={onFinish}
    >
      <Row gutter={24}>
        <Col lg={12}>
          <UserLookup 
            form={form} 
            selectedProfessional={initialProfessionalId as number} 
            selectedClient={initialClientId} 
          />
        </Col>
        <Col lg={12}>
          <Form.Item
            name="reportNumber"
            label="Report Number"
            rules={[{ required: true, message: 'Report Number is required' }]}
          >
            <Input placeholder="Enter report number" />
          </Form.Item>
          <Form.Item
            name="dateOfAnalysis"
            label="Date of Analysis"
            rules={[{ required: true, message: 'Date of Analysis is required' }]}
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="durationOfAnalysis"
            label="Duration of Analysis (days)"
            rules={[{ required: true, message: 'Duration is required' }]}
          >
            <Input placeholder="Enter duration" />
          </Form.Item>
        </Col>
      </Row>

      <Divider style={{ margin: '1rem 0' }} />
      <Row gutter={24}>
        <Col lg={12}>
          <Form.Item
            name="attachmentFile"
            label="Attachment"
            rules={[{ required: true, message: 'Attachment is required' }]}
          >
            <div>
              <Upload {...props}>
                <Button icon={<UploadOutlined />}>Click to Upload</Button>
              </Upload>
            </div>
          </Form.Item>
        </Col>
        <Col lg={12}>
          <Form.Item
            name="specialistReportFile"
            label="Specialist Report"
          >
            <div>
              <Upload {...specialistReportProps}>
                <Button icon={<UploadOutlined />}>Click to Upload</Button>
              </Upload>
            </div>
          </Form.Item>
        </Col>
      </Row>

      <StyledTabs
        type="editable-card"
        onChange={onChange}
        activeKey={activeKey}
        onEdit={onEdit}
        items={items}
      />
      <StyledForm.Item>
        <Button type="primary" htmlType="submit" loading={isSubmitting}>
          Save
        </Button>
      </StyledForm.Item>
    </StyledForm >
  )
}

export default HRVForm;
